import React, { useEffect, useMemo, useState } from "react";
import { Tabs, Tab, Box, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";

// Styling for the tabs container
const GradientTabs = styled(Tabs)({
  overflow: "hidden",
  "& .MuiTabs-indicator": {
    display: "none", // This will remove the indicator completely
  },
});

interface GradientTabProps {
  color: string; // Color for tab background
  isFirstTab: boolean;
  isLastTab?: boolean;
  tabStatus: "before" | "selected" | "after"; // Tab position relative to selected
  disabled: boolean; // Whether tab is disabled
  statusLabel?: string; // Optional label for status (e.g., "New Lead")
}

// Styled component for individual tabs
const GradientTab = styled(Tab)<GradientTabProps>(
  ({ color, isFirstTab, tabStatus, statusLabel, disabled, isLastTab }) => {
    const normalizedLabel = statusLabel?.toUpperCase();
    const isSurveyedTab = normalizedLabel === "SURVEYED";
    const isCompletedTab = normalizedLabel === "COMPLETED";
    const isProvisionalTab = normalizedLabel === "PROVISIONAL";
    const isStorageTab = normalizedLabel === "STORAGE";
    const useBlackStatusText =
      isSurveyedTab ||
      isCompletedTab ||
      isProvisionalTab ||
      isStorageTab;

    return {
    textTransform: "none",
    color: disabled
      ? "#7f7f7f"
      : tabStatus === "after"
        ? "#1F1F1F"
        : isLastTab && !isCompletedTab
          ? "#ffffff"
          : useBlackStatusText
            ? "#000000"
            : "#ffffff",
    fontWeight: 600,
    fontSize: "13px",
    width: "108px",
    padding: "0px 24px !important",
    paddingLeft: isFirstTab ? "none" : "40px !important",
    textAlign: "center",
    backgroundColor: disabled
      ? "#e9e9e9"
      : tabStatus === "selected"
        ? "blue"
        : tabStatus === "before"
          ? color
          : "#e9e9e9",
    opacity: 1,
    clipPath: isFirstTab
      ? "polygon(0% 0%, 75% 0%, 100% 50%, 75% 100%, 0% 100%)"
      : "polygon(75% 0%, 100% 50%, 75% 100%, 0% 100%, 25% 50%, 0% 0%)",
    transition: "transform 0.3s ease-in-out",
    marginLeft: isFirstTab ? "0px" : "-20px",
    "&.Mui-selected": {
      color: disabled ? "#7f7f7f" : useBlackStatusText ? "#000000" : "#ffffff",
      fontWeight: "bold",
      opacity: 1,
      backgroundColor: disabled ? "#e9e9e9" : color,
      border: "0px solid",
    },
    "&.Mui-selected:last-child": {
      color: isCompletedTab ? "#000000" : "#ffffff",
    },
    "&.Mui-focusVisible": {
      backgroundColor: "rgba(100, 95, 228, 0.32)",
    },
  };
});

interface RemovalTabProps {
  leadDetails: any;
}

const RemovalTab: React.FC<RemovalTabProps> = ({ leadDetails }) => {
  const status = leadDetails?.lead?.status;
  const normalizedStatus = status?.toUpperCase(); // normalize to uppercase
  const hasStatus = Boolean(status);
   /*
   * ---------------------------------------------------------
   * Status flags
   * ---------------------------------------------------------
   */
  const showStorageEnq =
    normalizedStatus === "STORAGE ENQ" || 
    normalizedStatus === "STORAGE_ENQ";
  const showStorage = normalizedStatus === "STORAGE";
   // Kept for compatibility with your existing logic.
  const isStorageFlow = showStorageEnq || showStorage;
  const isDebtorFlow = normalizedStatus === "DEBTOR";
  const isExternalFlow = normalizedStatus === "EXTERNAL";
  const showArchived = normalizedStatus === "ARCHIVED";
  const showProvisional = normalizedStatus === "PROVISIONAL";
  const showAwaiting = normalizedStatus ==="AWAITING";
  const showDispute = normalizedStatus ==="DISPUTE";
  const showLost = normalizedStatus ==='LOST';
/*
   * ---------------------------------------------------------
   * Base tab configuration
   * ---------------------------------------------------------
   */

const isSurveyCompleted =
    leadDetails?.lead?.survey_status === "COMPLETED";

    const baseTabLabels = useMemo(
    () => [
      "New Lead",
      "Estimate",
      isSurveyCompleted ? "Surveyed" : "Survey",
      "Quote",
      "Confirmed",
      "Job",
      "Completed",
    ],
    [isSurveyCompleted]
  );

  const baseTabColors = useMemo(
    () => [
      "#FF66C4",
      "#D16B2C",
      isSurveyCompleted ? "#00FFFF" : "#7ED957",
      "#FF3131",
      "#7C3014",
      "#a52a2a",
      "#FFF7D2",
    ],
    [isSurveyCompleted]
  );
  
  /*
   * ---------------------------------------------------------
   * Build tabs
   *
   * Important:
   * STORAGE ENQ:
   *   Storage Enq -> Storage
   *
   * STORAGE:
   *   New Lead -> Estimate -> Survey -> Quote ->
   *   Confirmed -> Job -> Storage
   *
   * This keeps Storage after Job instead of replacing
   * the complete normal flow.
   * ---------------------------------------------------------
   */
   const { tabLabels, tabColors } = useMemo(() => {
    let labels = [...baseTabLabels];
    let colors = [...baseTabColors];

    /*
     * Storage Enquiry flow
     * Keep your existing two-tab behavior.
     */
    if (showStorageEnq) {
      labels = ["Storage Enq", "Storage"];
      colors = ["#5500AA", "#6aa84f"];

      return {
        tabLabels: labels,
        tabColors: colors,
      };
    }

    /*
     * Debtor flow
     */
    if (isDebtorFlow) {
      return {
        tabLabels: ["Debtor"],
        tabColors: ["#FF66C4"],
      };
    }

    /*
     * External flow
     */
    if (isExternalFlow) {
      return {
        tabLabels: ["External"],
        tabColors: ["#FF66C4"],
      };
    }

    /*
     * Provisional
     * Insert after Confirmed.
     */
    if (showProvisional) {
      const index = labels.indexOf("Confirmed");

      if (index !== -1) {
        labels.splice(index + 1, 0, "Provisional");
        colors.splice(index + 1, 0, "#FFFF00");
      }
    }

    /*
     * Archived
     * Insert before Completed.
     */
    if (showArchived) {
      const index = labels.indexOf("Completed");

      if (index !== -1) {
        labels.splice(index, 0, "Archived");
        colors.splice(index, 0, "#666666");
      }
    }

    /*
     * Awaiting
     * Insert after Confirmed.
     */
    if (showAwaiting) {
      const index = labels.indexOf("Confirmed");

      if (index !== -1) {
        labels.splice(index + 1, 0, "Awaiting");
        colors.splice(index + 1, 0, "#0066ff");
      }
    }

    /*
     * Lost
     * Insert after Job.
     */
    if (showLost) {
      const index = labels.indexOf("Job");

      if (index !== -1) {
        labels.splice(index + 1, 0, "Lost");
        colors.splice(index + 1, 0, "#f6b26b");
      }
    }

    /*
     * Dispute
     * Insert after Job.
     */
    if (showDispute) {
      const index = labels.indexOf("Job");

      if (index !== -1) {
        labels.splice(index + 1, 0, "Dispute");
        colors.splice(index + 1, 0, "#000000");
      }
    }

    /*
     * STORAGE
     *
     * Important change:
     * Do NOT replace the normal flow.
     *
     * Add Storage after Job.
     *
     * Example:
     * New Lead -> Estimate -> Survey -> Quote ->
     * Confirmed -> Job -> Storage
     *
     * We intentionally remove Completed because Storage
     * is the current next stage in this flow.
     */
    if (showStorage) {
      const jobIndex = labels.indexOf("Job");

      if (jobIndex !== -1) {
        labels.splice(jobIndex + 1, 0, "Storage");
        colors.splice(jobIndex + 1, 0, "#6aa84f");
      }
    }

    return {
      tabLabels: labels,
      tabColors: colors,
    };
  }, [
    baseTabLabels,
    baseTabColors,
    showStorageEnq,
    showStorage,
    isDebtorFlow,
    isExternalFlow,
    showProvisional,
    showArchived,
    showAwaiting,
    showLost,
    showDispute,
  ]);

   /*
   * ---------------------------------------------------------
   * Status -> tab index
   * ---------------------------------------------------------
   */

 const getStatusIndex = () => {
  // Storage Enquiry flow
  if (showStorageEnq) {
    return 0;
  }

  // Debtor flow
  if (isDebtorFlow) {
    return 0;
  }

  // External flow
  if (isExternalFlow) {
    return 0;
  }

  // Storage flow
  if (showStorage) {
    const storageIndex = tabLabels.indexOf("Storage");
    return storageIndex !== -1 ? storageIndex : 0;
  }

  // Fixed status indexes for the normal flow
  const statusIndexMap: Record<string, number> = {
    NEW: 0,
    "NEW LEAD": 0,

    ESTIMATE: 1,
    ESTIMATES: 1,

    SURVEY: 2,
    SURVEYED: 2,

    QUOTE: 3,
    CONFIRMED: 4,
    JOB: 5,

    PROVISIONAL: tabLabels.indexOf("Provisional"),
    AWAITING: tabLabels.indexOf("Awaiting"),
    LOST: tabLabels.indexOf("Lost"),
    DISPUTE: tabLabels.indexOf("Dispute"),
    ARCHIVED: tabLabels.indexOf("Archived"),
    COMPLETED: tabLabels.indexOf("Completed"),
  };

  const index = statusIndexMap[normalizedStatus || ""];

  return typeof index === "number" && index >= 0 ? index : 0;
};

  

  // Setup initial tab value from status
  const [value, setValue] = useState(0);

  /*
   * Update selected tab whenever status or tab structure changes.
   */
  useEffect(() => {
    setValue(getStatusIndex());
  }, [
    normalizedStatus,
    tabLabels,
    showStorage,
    showStorageEnq,
    isDebtorFlow,
    isExternalFlow,
  ]);


 /*
   * Kept from your existing component.
   * Tabs are currently display-only because onChange
   * was commented in your original implementation.
   */
  const handleChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setValue(newValue);
  };

  /*
   * ---------------------------------------------------------
   * Lead flag
   * ---------------------------------------------------------
   */

  const getLeadFlag = () => {
    const followUpDate = leadDetails?.lead?.follow_up_date;
    const createdDate = leadDetails?.lead?.created_at;

    if (!followUpDate) {
      return null;
    }

    const today = new Date();
    const followUp = new Date(followUpDate);
    const created = createdDate
      ? new Date(createdDate)
      : today;

    const diffDays = Math.ceil(
      (followUp.getTime() - today.getTime()) /
        (1000 * 60 * 60 * 24)
    );

    const leadAge = Math.ceil(
      (today.getTime() - created.getTime()) /
        (1000 * 60 * 60 * 24)
    );

    if (diffDays === 0) {
      return "Pending Today";
    }

    if (diffDays > 0 && diffDays <= 7) {
      return "Contact Soon";
    }

    if (diffDays < 0 && leadAge < 30) {
      return "Overdue";
    }

    if (leadAge >= 30) {
      return "Old";
    }

    return "Pending";
  };

  const leadFlag = getLeadFlag();

  /*
   * ---------------------------------------------------------
   * Render
   * ---------------------------------------------------------
   */

  return (
    <GradientTabs
      sx={{
        padding: "6px 0px 6px 8px",
        borderTop: "1px solid #C4CDD5",
        position: "relative",
        zIndex: 999,
      }}
      value={value}
      // onChange={handleChange}
      aria-label="Removal lead status tabs"
    >
      {tabColors.map((color, index) => {
        const label = tabLabels[index];

        return (
          <GradientTab
            key={`${label}-${index}`}
            color={hasStatus ? color : "#7f7f7f"}
            isFirstTab={index === 0}
            isLastTab={index === tabColors.length - 1}
            tabStatus={
              index < value
                ? "before"
                : index === value
                  ? "selected"
                  : "after"
            }
            statusLabel={label}
            disabled={!hasStatus}
            label={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  position: "relative",
                }}
              >
                {index === 0 &&
                  (normalizedStatus === "NEW" ||
                    normalizedStatus === "NEW LEAD") &&
                  leadFlag && (
                    <Chip
                      label="Pending"
                      size="small"
                      sx={{
                        height: "14px",
                        fontSize: "8px",
                        fontWeight: 700,
                        backgroundColor: "#fff",
                        color,
                        border: `1px solid ${color}`,
                        letterSpacing: "0.5px",

                        "& .MuiChip-label": {
                          padding: "0 4px",
                        },
                      }}
                    />
                  )}

                <span>{label}</span>
              </Box>
            }
          />
        );
      })}
    </GradientTabs>
  );
};

export default RemovalTab;
